/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useEffect, useState} from "react";
import {ConfirmationDialog} from "../../../../Common/ConfirmationDialog";
import {GenericModelViewer} from "../../../../Common/GenericModelViewer";
import {Col, Row} from "react-bootstrap";
import {service as SpeciesService} from "../../../../../Services/SpeciesService";
import {useValidations} from "../../../../../Hooks/GenericModelHooks";
import {Species} from "../../../../../Models/StreamBank/Species";

export const SpeciesModal = (props) => {
	const {
		confirmationButtonMessage = "Save",
		cancelButtonMessage = "Cancel",
		deleteButtonMessage = null,
		disableConfirmationButton = null,
		disableDeleteButton = null,
		hiddenFields = [],
		onBeforeConfirm = null,
		onBeforeDelete = null,
		onHide = null,
		onReload= null,
		readOnly = false,
		show,
		species: speciesProp,
		title = 'Edit Species',
		summaryComponent: SummaryComponent = null,
		warning = null,
	} = props;

	const [speciesValidations, setSpeciesValidations, isValid] = useValidations();

	const [error, setError] = useState(null);
	const [species, setSpecies] = useState(speciesProp ?? {});

	const isNewSpecies = !(species?.speciesId);

	useEffect(() => {
		setSpecies(speciesProp ?? {});
	}, [speciesProp]);

	const saveSpecies = async (species) => {
		setError(null);
		try {
			const response = await (isNewSpecies ? SpeciesService.CreateSpecies(species) : SpeciesService.UpdateSpecies(species));
			if(response?.result) {
				onReload?.(response?.result);
				onHide?.();
			} else if (response.exception) {
				throw response.exception;
			} else {
				throw new Error(response?.message ?? 'Unknown Error occurred.');
			}
		} catch (error) {
			setError(error?.message);
		}
	}

	const onConfirm = async () => {
		setError(null);
		try {
			const speciesData = (await onBeforeConfirm?.(species)) ?? species;
			return saveSpecies(speciesData);
		} catch (error) {
			setError(error?.message ?? 'An error occurred, please try again.')
		}
	}

	const onDelete = async () => {
		setError(null);
		try {
			const speciesData = (await onBeforeDelete?.(species)) ?? species;
			return saveSpecies(speciesData);
		} catch (error) {
			setError(error?.message ?? 'An error occurred, please try again.')
		}
	}

	return <ConfirmationDialog
		size="lg"
		Title={title}
		Body={[
			(warning ?? error) &&
			<Row key="error-row">
				<Col xs={12}>
					<p className="sb-model-viewer-validation-failure-text">{warning ?? error}</p>
				</Col>
			</Row>,

			SummaryComponent &&
			<SummaryComponent
				key="summary"
				species={species}
			/>,

			<Row key="model-row">
				<Col xs={12}>
					<GenericModelViewer
						IsNewModel={isNewSpecies}
						Model={new Species()}
						EditMode={!readOnly}
						Data={species}
						Update={setSpecies}
						HideFields={hiddenFields ?? []}
						CustomData={{}}
						OnValidationUpdate={setSpeciesValidations}
					/>
				</Col>
			</Row>
		]}
		Display={show && species != null}
		OnHide={onHide}
		OnClose={onHide}
		CancelButtonMessage={cancelButtonMessage}
		OnConfirm={confirmationButtonMessage ? (() => void onConfirm()) : null}
		ConfirmationButtonMessage={confirmationButtonMessage}
		OnDelete={onBeforeDelete ? () => void onDelete() : null}
		DeleteButtonMessage={deleteButtonMessage}
		DisableConfirmationButton={(disableConfirmationButton?.(species)) || !isValid}
		DisableDeleteButton={disableDeleteButton?.(species)}
	/>;
}
